import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { CheckCircle, XCircle, Clock, AlertCircle, PlayCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

const TestingDashboard = () => {
  const navigate = useNavigate();
  const [testStatus, setTestStatus] = useState(null);
  const [testResults, setTestResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTestData();
  }, []);

  const loadTestData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      
      // Load test status summary
      const statusResponse = await axios.get(`${API}/admin/test-status`, {
        headers: { 'X-Admin-Token': token }
      });
      setTestStatus(statusResponse.data);

      // Load test results
      const resultsResponse = await axios.get(`${API}/admin/test-results`, {
        headers: { 'X-Admin-Token': token }
      });
      setTestResults(resultsResponse.data);
    } catch (error) {
      console.error('Error loading test data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'passed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-blue-600" />;
      case 'stuck':
        return <AlertCircle className="w-5 h-5 text-orange-600" />;
      default:
        return <PlayCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'passed': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'stuck': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading test dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Testing Dashboard</h1>
              <p className="text-sm text-gray-500">Monitor API and feature test results</p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={loadTestData}
                variant="outline"
              >
                Refresh
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/admin')}
              >
                ← Back to Admin
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Status Summary Cards */}
        {testStatus && (
          <div className="grid md:grid-cols-5 gap-4 mb-8">
            <Card className="p-6 text-center">
              <div className="text-3xl font-bold text-gray-900">{testStatus.total_tasks}</div>
              <div className="text-sm text-gray-600 mt-1">Total Tasks</div>
            </Card>
            <Card className="p-6 text-center bg-green-50">
              <div className="text-3xl font-bold text-green-600">{testStatus.passed}</div>
              <div className="text-sm text-gray-600 mt-1">Passed</div>
            </Card>
            <Card className="p-6 text-center bg-red-50">
              <div className="text-3xl font-bold text-red-600">{testStatus.failed}</div>
              <div className="text-sm text-gray-600 mt-1">Failed</div>
            </Card>
            <Card className="p-6 text-center bg-blue-50">
              <div className="text-3xl font-bold text-blue-600">{testStatus.in_progress}</div>
              <div className="text-sm text-gray-600 mt-1">In Progress</div>
            </Card>
            <Card className="p-6 text-center bg-orange-50">
              <div className="text-3xl font-bold text-orange-600">{testStatus.stuck}</div>
              <div className="text-sm text-gray-600 mt-1">Stuck</div>
            </Card>
          </div>
        )}

        {/* Test Results */}
        {testResults.length === 0 ? (
          <Card className="p-12 text-center">
            <PlayCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Test Results Yet</h3>
            <p className="text-gray-600">Test results will appear here once tests are run</p>
          </Card>
        ) : (
          <div className="space-y-6">
            {testResults.map((result, idx) => (
              <Card key={result.id || idx} className="p-6">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {result.user_problem_statement || 'Test Session'}
                  </h3>
                  <div className="text-sm text-gray-500">
                    {new Date(result.created_at).toLocaleString()}
                  </div>
                </div>

                {/* Backend Tasks */}
                {result.backend_tasks && result.backend_tasks.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Backend Tests</h4>
                    <div className="space-y-2">
                      {result.backend_tasks.map((task, taskIdx) => (
                        <div
                          key={taskIdx}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            {getStatusIcon(task.status)}
                            <div>
                              <div className="font-medium text-gray-900">{task.task_name}</div>
                              {task.file_path && (
                                <div className="text-xs text-gray-500">{task.file_path}</div>
                              )}
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                            {task.status.replace('_', ' ')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Frontend Tasks */}
                {result.frontend_tasks && result.frontend_tasks.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Frontend Tests</h4>
                    <div className="space-y-2">
                      {result.frontend_tasks.map((task, taskIdx) => (
                        <div
                          key={taskIdx}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            {getStatusIcon(task.status)}
                            <div>
                              <div className="font-medium text-gray-900">{task.task_name}</div>
                              {task.file_path && (
                                <div className="text-xs text-gray-500">{task.file_path}</div>
                              )}
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                            {task.status.replace('_', ' ')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TestingDashboard;
